import { Buffer } from 'buffer';

/**
 * Normalize XRPL currency codes from either standard 3-character format
 * or non-standard 40-character hexadecimal format into human-readable strings
 * Based on: https://github.com/XRPLF/xrpl-dev-portal/blob/master/_code-samples/normalize-currency-codes/js/normalize-currency-code.js
 */
export function normalizeCurrencyCode(currencyCode: string, maxLength = 20): string {
  if (!currencyCode) return "";
  
  // Handle standard 3-character code
  if (currencyCode.length === 3) {
    return currencyCode.trim();
  }

  // Handle hexadecimal currency code
  if (currencyCode.match(/^[a-fA-F0-9]{40}$/) && !isNaN(parseInt(currencyCode, 16))) {
    const hex = currencyCode.toString().replace(/(00)+$/g, '');

    // Handle demurrage codes (old standard)
    if (hex.startsWith('01')) {
      return convertDemurrageToUTF8(currencyCode);
    }

    // Handle XLS-16d NFT Metadata
    if (hex.startsWith('02')) {
      const xls15d = Buffer.from(hex, 'hex').slice(8).toString('utf-8').slice(0, maxLength).trim();
      if (xls15d.match(/[a-zA-Z0-9]{3,}/) && xls15d.toLowerCase() !== 'xrp') {
        return xls15d;
      }
    }

    // Handle standard hex-encoded ASCII/UTF-8
    const decodedHex = Buffer.from(hex, 'hex').toString('utf-8').slice(0, maxLength).trim();
    if (decodedHex.match(/[a-zA-Z0-9]{3,}/) && decodedHex.toLowerCase() !== 'xrp') {
      return decodedHex;
    }
  }

  return "";
}

/**
 * Convert demurrage currency code to human-readable format with interest rate
 */
function convertDemurrageToUTF8(demurrageCode: string): string {
  const bytes = Buffer.from(demurrageCode, "hex");
  const code = String.fromCharCode(bytes[1]) + 
               String.fromCharCode(bytes[2]) + 
               String.fromCharCode(bytes[3]);
  
  const interest_start = (bytes[4] << 24) + 
                        (bytes[5] << 16) + 
                        (bytes[6] << 8) + 
                        (bytes[7]);
  
  const interest_period = bytes.readDoubleBE(8);
  const year_seconds = 31536000; // Fixed number of seconds per year in XRP Ledger
  
  const interest_after_year = Math.pow(Math.E, 
    (interest_start + year_seconds - interest_start) / interest_period);
  const interest = (interest_after_year * 100) - 100;
  
  return `${code} (${interest.toFixed(1)}% pa)`;
} 